/**
 * ChatComposer — unified input bar with billing gate, attachments, and send.
 *
 * The thin composer's "+" (labelled Attach / Add more, with a tooltip) opens
 * ChatAttachModal (drag-and-drop + finder + a landing-page URL field with "+").
 * Attachments preview in the tray above the input:
 *   - uploaded images (compressed client-side) as thumbnails
 *   - a PDF chip (text extracted client-side)
 *   - landing-page URL captures as async chips that load in place, then can be
 *     clicked to scroll the full page / redo / validate
 * On send, ready URL captures are folded into the images array (as base64 PNG)
 * alongside the uploaded images.
 *
 * VIDEO creatives (MP4/WebM/MOV) attach through the same modal: the tile previews from
 * the in-memory base64 while the raw bytes are hosted (send is blocked with an
 * "Uploading video…" hint until the hosted url lands), and on send each hosted video
 * rides the payload as `stimulusVideos: [{url, label?, mimeType?}]`, never as a base64
 * entry of the images array.
 *
 * A pasted direct video URL (https://cdn.agency.com/spot.mp4) needs no hosting step:
 * it is already a public https ref, so it joins `stimulusVideos` straight from the
 * capture list. It must NOT be looked for in `readyCaptures`, which selects on
 * `screenshotBase64` and would drop it without a word.
 */

import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileText, Plus, X } from 'lucide-react'
import { toast } from 'sonner'

import { ChatComposer as ChatComposerPrimitive } from '@/components/chat/ChatComposer'
import { TellerCountSelector } from '@/components/chat/TellerCountSelector/TellerCountSelector'
import { RoomCreditQuote } from '@/components/chat/ConversationCreditQuote/ConversationCreditQuote'
import { ConversationModelPicker } from '@/components/chat/ConversationModelPicker/ConversationModelPicker'
import { ChatAttachModal } from '@/components/chat/ChatAttachModal'
import { useChatFileIngest, type UploadHosted } from '@/components/chat/useChatFileIngest'
import { CreativeMedia, isVideoCreativeLike } from '@/components/chat/CreativeMedia'
import {
  hasVideoStillHosting,
  isVideoChatImage,
  toStimulusVideos,
  uploadHostedChatVideo,
  type StimulusVideo,
} from '@/components/chat/stimulus-videos'
import { CapturedImageChip } from '@/components/media/CapturedImageChip'
import { useUrlCaptures, videoUrlFromCapture } from '@/components/media/useUrlCaptures'
import { LockedFreeGate } from '@/components/billing/LockedFreeGate'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useChatBlockReason } from '@/lib/hooks/use-chat-block-reason'
import { compressCaptureForChat } from '@/lib/utils/compress-image'
import { ROUTES } from '@/routes/routes'
import { mediaApi, type ChatImage } from '@/lib/api'

import { ChatRoomEffectiveConversation, ChatRoomStatus, ConversationLevel, ConversationModel, directVideoMimeType, mentionsAll } from '@digital-twins/shared-types'
import type { MentionParticipant } from '@/components/chat/MentionAutocomplete'
import { HandleSendOptions } from '../useChatRoom'

// Backend caps a chat message at 3 images (ArrayMaxSize). Enforce the SAME cap
// here across uploaded images + URL captures COMBINED, so the UI never stages
// more than what can actually be sent.
const MAX_ATTACHMENTS = 3
const MAX_FILE_MB = 10
// Keep the total message body under the backend's /chat-rooms 10mb json limit.
// base64 inflates ~4/3, so ~6.5MB raw of images → ~8.7MB base64, leaving room
// for the text + the JSON envelope. Captures are budgeted by count against this.
const CHAT_BODY_BUDGET_RAW = 6_500_000
const PER_CAPTURE_MAX_RAW = 3_800_000

/**
 * The `[Document: name]` block a text-extracted PDF adds to the message content.
 * The closing `[/Document]` line lets the thread show a document chip plus only the
 * user's own text (Step 11). Twins read the whole string.
 */
export function buildPdfInlinedContent(pdfName: string | null, pdfText: string, text: string): string {
  return `[Document: ${pdfName}]\n\n${pdfText}\n[/Document]\n\n${text}`.trim()
}

/**
 * Narrow screens keep the text box usable: the per-message price (also listed per level in the
 * level menu) only shows from 480px up, where the row has room for it.
 */
export const ROOM_CREDIT_QUOTE_CLASS = 'hidden min-[480px]:inline-flex'

/** First PDF carried on a rendered tile, as the send body's `documents` (one per message, Step 10c). */
export function collectSendDocuments(images: Array<ChatImage>): HandleSendOptions['documents'] {
  const source = images.find((image) => image.sourceDocument)?.sourceDocument
  return source ? [source] : undefined
}

/**
 * Pairing contract with Materials: when a message carries a document, its rendered
 * image is the message's FIRST image, so the pane lists the pair once (as the
 * document) without guessing. Order of the other attachments is kept.
 */
export function orderRenderFirst(images: Array<ChatImage>): Array<ChatImage> {
  const renderIndex = images.findIndex((image) => image.sourceDocument)
  if (renderIndex <= 0) {
    return images
  }
  return [images[renderIndex], ...images.slice(0, renderIndex), ...images.slice(renderIndex + 1)]
}

/**
 * Room page wiring (Step 11b/11c). Present only on the authenticated room page:
 * guest rooms accept neither teller picks nor documents, so they never pass it.
 */
export interface RoomComposerBinding {
  /** Replaces `onSend`; carries the PDF documents. The page adds the teller picks. */
  onSend: (
    content: string,
    images?: Array<ChatImage>,
    captureUrl?: string,
    stimulusVideos?: Array<StimulusVideo>,
    options?: Pick<HandleSendOptions, 'documents'>,
  ) => void
  /** Participant row ids of the @mentions linked in the text. */
  onMentionsChange?: (participantIds: Array<string>) => void
  /** A mention the pane asked to unlink (text kept). */
  mentionUnlinkRequest?: { participantId: string; nonce: number } | null
  /** A mention the profile card's Ask action asked to insert (participant row id). */
  mentionPrefillRequest?: { participantId: string; nonce: number } | null
  /** Why the room cannot take a send right now (all twins off); a message with `@all` still sends (D4). */
  sendBlockedHint?: string
  /**
   * "Speaking first" in the send cluster: the room's `tellerCount`, the same setting the
   * pane's stepper writes. The page passes it only to the owner of a group room.
   */
  speakers?: RoomComposerSpeakers
  /**
   * Conversation model picker (Low / Medium / High, plus the locked advanced list for the
   * entitled). The page passes it editable to the room's owner and read-only to a signed-in
   * collaborator; the picker decides from the viewer's flags what, if anything, renders.
   */
  conversationModel?: RoomComposerConversationModel
  /**
   * Called once a video the user attached has finished hosting, so the room can start the
   * twins watching it while the user types. Fire-and-forget; the composer swallows errors.
   */
  onVideoHosted?: (videoUrl: string) => void
}

/** Room videos are capped at 5 minutes (the server watch timeouts are sized to it). */
export const ROOM_VIDEO_MAX_SECONDS = 300

/**
 * Wraps the video hoster so a room can react once the upload resolves. The callback is
 * best-effort and never changes what the upload returns.
 */
export function withVideoHostedCallback(
  upload: UploadHosted,
  onVideoHosted: ((videoUrl: string) => void) | undefined,
): UploadHosted {
  if (!onVideoHosted) {
    return upload
  }
  return async (image) => {
    const hosted = await upload(image)
    if (hosted?.url) {
      try {
        onVideoHosted(hosted.url)
      } catch {
        // prewatch is an optimisation, never a failure
      }
    }
    return hosted
  }
}

export interface RoomComposerConversationModel {
  level: ConversationLevel
  /** The stored advanced pick (`settings.conversationModel`), when present. */
  advancedModel?: ConversationModel | null
  /** The room DTO's `creditsPerMessage`; undefined hides the prices. */
  baseRate?: number | null
  /** A collaborator sees the owner's level and price, with no menu. */
  isReadOnly?: boolean
  /** The room DTO's `effectiveConversation` (owner's gates); the read-only view's only source. */
  effectiveConversation?: ChatRoomEffectiveConversation | null
  isDisabled?: boolean
  onLevelChange?: (level: ConversationLevel) => void
  onAdvancedChange?: (model: ConversationModel) => void
}

export interface RoomComposerSpeakers {
  value: number
  min: number
  max: number
  isDisabled: boolean
  onChange: (value: number) => void
}

interface ChatComposerProps {
  /**
   * `captureUrl` is unused by this composer (captures fold into `images`) but keeps the
   * positional contract of useChatRoom's handleSend, so `stimulusVideos` rides fourth.
   * Videos are hosted refs per the wire contract: `[{url, label?, mimeType?}]`.
   */
  onSend: (
    content: string,
    images?: Array<ChatImage>,
    captureUrl?: string,
    stimulusVideos?: Array<StimulusVideo>,
  ) => void
  roomStatus: ChatRoomStatus
  placeholder?: string
  /** Room participants for @mention autocomplete. */
  participants?: Array<MentionParticipant>
  roomComposer?: RoomComposerBinding
  /** The larger Home Lab look, for the composer centered in an empty conversation. */
  hero?: boolean
  /** A ready-to-send suggested message, typed in as the placeholder (the room's opening question). */
  suggestion?: string | null
}

export function ChatComposer(props: ChatComposerProps) {
  const { onSend, roomStatus, placeholder, participants, roomComposer, hero = false, suggestion = null } = props
  const { t } = useTranslation('app')

  const blockReason = useChatBlockReason()

  const [pendingImages, setPendingImages] = useState<Array<ChatImage>>([])
  const [pdfText, setPdfText] = useState<string | null>(null)
  const [pdfName, setPdfName] = useState<string | null>(null)
  const [attachOpen, setAttachOpen] = useState(false)
  const [isAttachTipOpen, setIsAttachTipOpen] = useState(false)
  /* Closing the modal hands focus back to the "+", and a focused trigger opens its
     tooltip: the first open after a close is that hand-back, so it is skipped. */
  const isSkippingNextTipOpenRef = useRef(false)
  const [isSending, setIsSending] = useState(false)
  const sendingRef = useRef(false)
  // Chat follows the study link rules: a YouTube link is refused with the study
  // message (each twin watches the file itself, which a watch URL cannot give).
  const captures = useUrlCaptures({
    capture: mediaApi.captureUrl,
    scale: 1,
    max: MAX_ATTACHMENTS,
    allowYoutubeVideo: false,
  })

  const isClosed = roomStatus === 'closed'

  const readyCaptures = useMemo(
    () => captures.captures.filter((c) => c.status === 'ready' && c.result?.screenshotBase64),
    [captures.captures],
  )

  /**
   * Direct video file URLs among the captures. These have no screenshot by
   * design, so `readyCaptures` cannot see them: they ride out as hosted
   * `stimulusVideos` refs instead, exactly like an uploaded video that has
   * finished hosting.
   */
  const captureVideoUrls = useMemo(
    () =>
      captures.captures
        .map((c) => videoUrlFromCapture(c))
        .filter((url): url is string => Boolean(url)),
    [captures.captures],
  )

  const hasAttachments =
    pendingImages.length > 0 ||
    pdfText !== null ||
    readyCaptures.length > 0 ||
    captureVideoUrls.length > 0

  const handlePdfChange = useCallback((text: string | null, name: string | null) => {
    setPdfText(text)
    setPdfName(name)
  }, [])

  const roomOnVideoHosted = roomComposer?.onVideoHosted
  const roomUploadHosted = useMemo(
    () => withVideoHostedCallback(uploadHostedChatVideo, roomOnVideoHosted),
    [roomOnVideoHosted],
  )

  // Shared ingest path so paste/drop and the attach modal apply identical
  // compression/PDF/cap rules while sharing the URL-capture attachment budget.
  // Video: accepted and HOSTED on ingest (uploadHostedChatVideo is video-only; images
  // keep the inline base64 path). `onImagesMerge` is the same React setter so the video
  // hosting window's late emit merges into the current list instead of clobbering it,
  // and a video whose hosting failed is dropped with a toast rather than riding as
  // base64 (see useChatFileIngest).
  const { ingestFiles, pendingCount: filePendingCount, pdfDisplayName, clearPdfDisplayName } = useChatFileIngest({
    images: pendingImages,
    onImagesChange: setPendingImages,
    onImagesMerge: setPendingImages,
    onPdfChange: handlePdfChange,
    maxImages: Math.max(0, MAX_ATTACHMENTS - captures.count),
    maxSizeMB: MAX_FILE_MB,
    uploadHosted: roomUploadHosted,
    acceptVideo: true,
    maxVideoSeconds: roomComposer ? ROOM_VIDEO_MAX_SECONDS : undefined,
    keepPdfSource: Boolean(roomComposer),
  })

  // A video with no hosted url yet: block send (its bytes never ride the payload, so a
  // send now would run the turn without the stimulus). Same gate as Quick Study.
  const videoStillHosting = hasVideoStillHosting(pendingImages)

  /* What the tray shows, a wider set than `hasAttachments` (which gates sending):
     uploads still in flight, captures in any state and the PDF chip. */
  const hasTrayContent =
    hasAttachments || filePendingCount > 0 || captures.captures.length > 0 || Boolean(pdfName)

  const handleAttachOpenChange = useCallback((isOpen: boolean) => {
    setAttachOpen(isOpen)
    if (!isOpen) {
      // Only the focus return that follows the close is skipped. Browsers that do
      // not refocus the trigger (Safari) must not swallow a later hover.
      isSkippingNextTipOpenRef.current = true
      window.setTimeout(() => {
        isSkippingNextTipOpenRef.current = false
      }, 300)
      setIsAttachTipOpen(false)
    }
  }, [])

  const handleAttachTipOpenChange = useCallback((isOpen: boolean) => {
    if (isOpen && isSkippingNextTipOpenRef.current) {
      isSkippingNextTipOpenRef.current = false
      return
    }
    setIsAttachTipOpen(isOpen)
  }, [])

  const removeImage = useCallback(
    (index: number) => setPendingImages((prev) => prev.filter((_, i) => i !== index)),
    [],
  )

  const handleSend = useCallback(
    async (text: string) => {
      // Synchronous re-entrancy guard: the shared primitive fires onSend without
      // awaiting, so a rapid second Enter/click during the compress await would
      // otherwise send the same attachments twice.
      if (sendingRef.current) return
      sendingRef.current = true
      setIsSending(true)
      try {
        let content = text
        if (pdfText) {
          content = buildPdfInlinedContent(pdfName, pdfText, text)
        }

        // A capture the user added that hasn't finished is dropped on send —
        // tell them rather than silently losing it.
        const stillLoading = captures.captures.filter((c) => c.status === 'loading').length
        if (stillLoading > 0) {
          toast(`${stillLoading} page${stillLoading > 1 ? 's were' : ' was'} still capturing — not attached`)
        }

        // Captures are full-page PNGs that can exceed the chat API's image cap;
        // re-encode each as a bounded JPEG (uploaded images are already
        // compressed in handleFiles). Budget each capture by count so the SUM of
        // all images stays under the /chat-rooms body limit — not just each one.
        let captureImages: Array<ChatImage> = []
        if (readyCaptures.length > 0) {
          // Videos never ride the JSON body (hosted refs only), so they must not eat
          // the capture byte budget.
          const pendingRaw = pendingImages
            .filter((img) => !isVideoChatImage(img))
            .reduce((sum, img) => sum + Math.ceil((img.base64.length * 3) / 4), 0)
          const perCapture = Math.min(
            PER_CAPTURE_MAX_RAW,
            Math.max(
              500_000,
              Math.floor((CHAT_BODY_BUDGET_RAW - pendingRaw) / readyCaptures.length),
            ),
          )
          // Compress independently so one bad capture never drops the whole
          // message — skipped pages are surfaced, not swallowed.
          const settled = await Promise.allSettled(
            readyCaptures.map((c) =>
              compressCaptureForChat(c.result!.screenshotBase64 as string, perCapture),
            ),
          )
          captureImages = settled
            .filter((s): s is PromiseFulfilledResult<ChatImage> => s.status === 'fulfilled')
            .map((s) => s.value)
          const failed = settled.length - captureImages.length
          if (failed > 0) {
            toast.error(`Couldn't attach ${failed} captured page${failed > 1 ? 's' : ''}`)
          }
        }
        // Split the attachment list per the wire contract: videos leave as hosted
        // `stimulusVideos` refs; only true images ride inline. Outgoing images are
        // narrowed to `{base64, mimeType}` so a hosted ref or note can never trip the
        // backend's whitelist validation.
        // Uploaded-then-hosted videos, plus any pasted direct video URLs. The
        // latter are already public https refs, so they need no hosting gate.
        const stimulusVideos = [
          ...toStimulusVideos(pendingImages),
          ...captureVideoUrls.map((url) => {
            const mimeType = directVideoMimeType(url)
            return { url, ...(mimeType ? { mimeType } : {}) }
          }),
        ]
        const unhostedVideos = pendingImages.filter(
          (im) => isVideoChatImage(im) && !im.url,
        ).length
        if (unhostedVideos > 0) {
          // The send gate blocks this normally; if a stray unhosted video is still here,
          // say it was not attached instead of silently dropping it (its base64 must
          // never ride the message).
          toast.error(
            `${unhostedVideos} video${unhostedVideos > 1 ? 's were' : ' was'} still uploading — not attached`,
          )
        }
        const orderedPending = roomComposer ? orderRenderFirst(pendingImages) : pendingImages
        // Room sends mark each landing-page capture so the backend slices it as
        // studies do; guest sends keep the plain image shape their route takes.
        const markedCaptures = roomComposer
          ? captureImages.map((image) => ({ ...image, presentation: 'landing_page' as const }))
          : captureImages
        const images = [...orderedPending, ...markedCaptures]
          .filter((im) => !isVideoChatImage(im))
          .map(({ base64, mimeType, presentation }) => ({
            base64,
            mimeType,
            ...(presentation ? { presentation } : {}),
          }))
        if (roomComposer) {
          const documents = collectSendDocuments(pendingImages)
          roomComposer.onSend(
            content,
            images.length > 0 ? images : undefined,
            undefined,
            stimulusVideos.length > 0 ? stimulusVideos : undefined,
            documents ? { documents } : undefined,
          )
        } else {
          onSend(
            content,
            images.length > 0 ? images : undefined,
            undefined,
            stimulusVideos.length > 0 ? stimulusVideos : undefined,
          )
        }
        setPendingImages([])
        setPdfText(null)
        setPdfName(null)
        clearPdfDisplayName()
        captures.clear()
      } finally {
        sendingRef.current = false
        setIsSending(false)
      }
    },
    [onSend, roomComposer, pendingImages, readyCaptures, captureVideoUrls, pdfText, pdfName, captures, clearPdfDisplayName],
  )

  if (blockReason === 'out_of_credits') {
    return (
      <div className="shrink-0 px-4 pb-4" data-testid="chat-composer-blocked">
        <LockedFreeGate
          inline
          action="chat with twins"
          ctaLabel={t('chatChrome.goBilling')}
          href={ROUTES.settingsTab('billing')}
          description="You're out of credits. Enable overage or wait for renewal."
        />
      </div>
    )
  }

  if (blockReason !== 'none') {
    return (
      <div className="shrink-0 px-4 pb-4" data-testid="chat-composer-blocked">
        <LockedFreeGate inline action="chat with twins" ctaLabel={t('chatChrome.upgradeToChat')} />
      </div>
    )
  }

  const roomBlockedHint = roomComposer?.sendBlockedHint
  const speakers = roomComposer?.speakers
  const conversationModel = roomComposer?.conversationModel

  const captureErrors = [
    ...new Set(
      captures.captures.flatMap((capture) =>
        capture.status === 'error' && capture.error ? [capture.error] : [],
      ),
    ),
  ]

  return (
    <div className="shrink-0 px-4 pb-4">
      {captureErrors.length > 0 && (
        <div data-testid="chat-capture-error" className="mb-2 flex flex-col gap-1">
          {captureErrors.map((message) => (
            <p key={message} className="text-sm text-[#E5484D]">
              {message}
            </p>
          ))}
        </div>
      )}
      <ChatComposerPrimitive
        onSend={handleSend}
        disabled={isClosed || isSending}
        placeholder={placeholder ?? t('chatChrome.messagePlaceholder')}
        compactPlaceholder={placeholder ? t('chatChrome.messagePlaceholder') : undefined}
        requireText={!hasAttachments}
        sendBlockedHint={videoStillHosting ? 'Uploading video…' : roomBlockedHint}
        liftsSendBlock={videoStillHosting || !roomBlockedHint ? undefined : mentionsAll}
        sendBlockedHintDataTestId={
          !videoStillHosting && roomBlockedHint ? 'chat-send-blocked-hint' : undefined
        }
        participants={participants}
        onMentionsChange={roomComposer?.onMentionsChange}
        mentionUnlinkRequest={roomComposer?.mentionUnlinkRequest}
        mentionPrefillRequest={roomComposer?.mentionPrefillRequest}
        beforeSendSlot={
          speakers || conversationModel ? (
            <>
              {conversationModel ? (
                <ConversationModelPicker
                  level={conversationModel.level}
                  advancedModel={conversationModel.advancedModel}
                  baseRate={conversationModel.baseRate}
                  isReadOnly={conversationModel.isReadOnly}
                  effectiveConversation={conversationModel.effectiveConversation}
                  disabled={conversationModel.isDisabled}
                  onLevelChange={conversationModel.onLevelChange}
                  onAdvancedChange={conversationModel.onAdvancedChange}
                  testIdPrefix="chat-composer-model"
                />
              ) : null}
              {conversationModel && !conversationModel.isReadOnly ? (
                <RoomCreditQuote
                  baseRate={conversationModel.baseRate}
                  level={conversationModel.level}
                  advancedModel={conversationModel.advancedModel}
                  className={ROOM_CREDIT_QUOTE_CLASS}
                />
              ) : null}
              {speakers ? (
                <TellerCountSelector
                  variant="pill"
                  testIdPrefix="composer-speakers"
                  value={speakers.value}
                  min={speakers.min}
                  max={speakers.max}
                  isDisabled={speakers.isDisabled}
                  onChange={speakers.onChange}
                />
              ) : null}
            </>
          ) : undefined
        }
        dataTestId="chat-thread-composer"
        hero={hero}
        suggestion={hasAttachments ? null : suggestion}
        textareaDataTestId="chat-thread-composer-input"
        density="thin"
        attachmentsSlot={
          hasTrayContent ? (
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
              {/* Image / video thumbnails. CreativeMedia renders the same <img> for images
                  and a muted preview for videos; a hosted video streams from its url
                  instead of re-materializing a giant data: URL. */}
              {pendingImages.map((img, index) => (
                <div key={index} className="group relative">
                  <CreativeMedia
                    src={
                      isVideoCreativeLike(img) && img.url
                        ? img.url
                        : `data:${img.mimeType};base64,${img.base64}`
                    }
                    mimeType={img.mimeType}
                    alt={`Upload ${index + 1}`}
                    className="h-12 w-12 rounded-lg border border-border object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    disabled={isClosed}
                    className="absolute -right-1.5 -top-1.5 rounded-full bg-destructive p-0.5 text-destructive-foreground opacity-0 transition-opacity group-hover:opacity-100 disabled:opacity-50"
                    aria-label={`Remove upload ${index + 1}`}
                    data-testid={`chat-file-upload-remove-${index}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}

              {Array.from({ length: filePendingCount }).map((_, index) => (
                <div
                  key={`skeleton-${index}`}
                  data-testid={`chat-file-upload-skeleton-${index}`}
                  aria-hidden="true"
                  className="h-12 w-12 animate-pulse rounded-lg border border-border bg-muted"
                />
              ))}

              {/* URL capture chips (loading → thumbnail → click to review) */}
              {captures.captures.map((c) => (
                <CapturedImageChip
                  key={c.id}
                  capture={c}
                  className="h-12 w-12"
                  onRedo={() => captures.redo(c.id)}
                  onValidate={() => captures.validate(c.id)}
                  onRemove={() => captures.remove(c.id)}
                />
              ))}

              {/* PDF chip */}
              {pdfName && (
                <div className="group relative inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground">
                  <FileText className="h-3 w-3 shrink-0" />
                  <span className="max-w-[120px] truncate">{pdfDisplayName ?? pdfName}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setPdfText(null)
                      setPdfName(null)
                      clearPdfDisplayName()
                    }}
                    disabled={isClosed}
                    className="ml-0.5 rounded-full p-0.5 opacity-0 transition-opacity hover:bg-destructive/20 group-hover:opacity-100"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </div>
              )}
            </div>
          ) : null
        }
        leadingSlot={
          <>
            <TooltipProvider delayDuration={400}>
              <Tooltip open={isAttachTipOpen && !attachOpen} onOpenChange={handleAttachTipOpenChange}>
                <TooltipTrigger
                  type="button"
                  onClick={() => {
                    isSkippingNextTipOpenRef.current = false
                    setAttachOpen(true)
                  }}
                  disabled={isClosed}
                  data-testid="chat-attach-button"
                  aria-label={hasAttachments ? t('chatChrome.addMore') : t('chatChrome.attach')}
                  className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-twin-chat-fg-muted transition-colors after:absolute after:-inset-1 after:content-[''] hover:bg-twin-chat-fg/10 hover:text-twin-chat-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:pointer-events-none disabled:opacity-50"
                >
                  <Plus className="h-5 w-5" aria-hidden="true" />
                </TooltipTrigger>
                <TooltipContent side="top">{t('chatChrome.attachTooltip')}</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <ChatAttachModal
              open={attachOpen}
              onOpenChange={handleAttachOpenChange}
              onFiles={ingestFiles}
              onAddUrls={(urls) => urls.forEach((u) => captures.addUrl(u))}
              maxUrls={Math.max(0, MAX_ATTACHMENTS - pendingImages.length - captures.count)}
              disabled={isClosed}
              acceptVideo
            />
          </>
        }
      />
    </div>
  )
}
